import { MarkerMapa } from './../../../../../auxiliares/mapa-facil/interfaces';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
  inject,
} from '@angular/core';
import {
  IConfigReenvio,
  IFilter,
  IPopulate,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
  IReporteTracker4G,
  IReporteTrackerQualcomm,
  IReporteTrackerT1000B,
  ITracker,
} from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { SimsService } from '../../../../../auxiliares/servicios/http/sims.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { MapaFacilService } from '../../../../../auxiliares/mapa-facil/mapa-facil.service';
import { MapCardConfig } from '../../../../../componentes/map-card/map-card.component';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { TrackersService } from '../trackers.service';
import {
  configReenvioVigente,
  IReenviosTrackerData,
  ReenviosTrackerDialogComponent,
} from '../reenvios-tracker-dialog/reenvios-tracker-dialog.component';
import {
  etiquetaProtocoloTracker,
  nombreProtocoloTracker,
  protocoloDetectado,
} from '../../../../../auxiliares/protocolos-tracker.util';
import {
  EstadoConexion,
  duracion,
  estadoConexion,
  haceCuanto,
  minutosDesde,
} from '../../../../../auxiliares/estado-conexion.util';
import { SimDetalle } from '../../../../../componentes/detalle/detalle.components';

type EstadoProtocolo = 'coincide' | 'difiere' | 'sin-declarar' | 'sin-datos';

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaTracker {
  marcaModelo: string;
  asignadoA?: string;
  estadoCuenta?: string;
  uniqueId?: string;
  telefono?: string;
  numeroAbonado?: string;
  numeroSerie?: string;
  /** frecReporte legible: "5 min", "23 h 59 min", "2 d". */
  frecuencia?: string;
  conexion: EstadoConexion;
  ultimoReporte?: string;
  hace?: string;
  /** Solo GPRS: los que entran por trackers-go. */
  protocolo?: {
    declarado?: string;
    detectado?: string;
    estado: EstadoProtocolo;
    mensaje?: string;
  };
  /** Solo con permiso para ver reenvíos. */
  reenvios?: { activas: number; total: number };
  sims: SimDetalle[];
  /** Solo T1000-B: el tracker y el nodo LoRaWAN son el mismo equipo. */
  lora?: {
    deveui?: string;
    joineui?: string;
    senal?: string;
    ultimaComunicacion?: string;
    tasaEntrega?: string;
    ultimoGateway?: string;
    snrGateway?: number;
    limiteComunicacion?: number;
    habilitado: boolean;
  };
}

@Component({
  selector: 'app-detalles-tracker-info',
  templateUrl: './detalles-tracker-info.component.html',
  styleUrl: './detalles-tracker-info.component.scss',
  standalone: false,
})
export class DetallesTrackerInfoComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private route = inject(ActivatedRoute);
  private mapaService = inject(MapaFacilService);
  private formularios = inject(FormularioDialogService);
  private trackersService = inject(TrackersService);
  private simsService = inject(SimsService);

  public id: string | null = null;
  public tracker: ITracker;
  public reporte: IReporteGenerico;
  /** El último reporte de cualquier tipo: la fecha de "Último reporte". */
  private reporteReciente?: IReporteGenerico;
  /** Último reporte de posición: trae el protocolo detectado. */
  private reporte4G?: IReporteBase<'Tracker 4G'>;
  public trackers$: Subscription;
  public reportes$: Subscription;
  public configReenvios$: Subscription;

  public loading = true;
  public loadingMap = signal(true);

  public vista?: VistaTracker;
  public mapCardConfig: MapCardConfig;

  /**
   * En pantallas anchas la vista ocupa todo el alto de la pestaña (lo da el
   * detalle, que mide el alto del panel) y el mapa se estira hasta abajo. En
   * angostas va todo en una columna.
   */
  public pantallaAncha = signal(window.innerWidth > 900);

  /**
   * Alto de la columna del mapa, en px. map-card le resta su encabezado al
   * alto que recibe; con "100%" lo restaría dos veces (el 100% ya es lo que
   * queda debajo del encabezado) y quedaría un hueco abajo. Que Mapbox se
   * redimensione con el nuevo alto lo resuelve map-card.
   */
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('contenedorMapa') set contenedorMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 900);
  }

  /// Reenvíos que aplican a este tracker
  private configsReenvio: IConfigReenvio[] = [];
  private configsReenvioPedidas = false;
  private puedeVerReenvios = this.helper.tienePermiso(
    'Administración - Ver configuraciones de reenvíos',
  );

  public avisarProtocolo(): void {
    if (this.vista?.protocolo?.mensaje) {
      this.helper.notifWarn(this.vista.protocolo.mensaje);
    }
  }

  /**
   * Arma la vista con lo que haya llegado. Se llama desde los tres caminos de
   * carga (tracker, último reporte y reenvíos), que terminan en cualquier orden.
   */
  private armarVista(): void {
    const t = this.tracker;
    if (!t) return;

    // Conexión y último reporte con el más nuevo de cualquier tipo (un reporte
    // de combustible también es el equipo comunicando); el mapa usa el de
    // posición (this.reporte).
    const fecha =
      this.reporteReciente?.valores?.timestamp ??
      this.reporteReciente?.fechaCreacion;
    const minutos = minutosDesde(fecha);

    // Protocolo declarado (modelo) vs. detectado (último reporte "Tracker 4G",
    // lo estampa trackers-go). Solo para los que entran por trackers-go.
    let protocolo: VistaTracker['protocolo'];
    if (t.tipo === 'GPRS') {
      const declarado = t.modelo?.protocoloTracker;
      const detectado = this.reporte4G?.valores?.protocolo;
      const estado: EstadoProtocolo = !detectado
        ? 'sin-datos'
        : !declarado
          ? 'sin-declarar'
          : declarado === detectado
            ? 'coincide'
            : 'difiere';
      protocolo = {
        declarado: declarado ? etiquetaProtocoloTracker(declarado) : undefined,
        detectado: protocoloDetectado(this.reporte4G?.valores),
        estado,
        mensaje:
          estado === 'difiere'
            ? `El equipo reporta como ${nombreProtocoloTracker(detectado)}, ` +
              `pero su modelo declara ${nombreProtocoloTracker(declarado)}. ` +
              'Revisar el puerto configurado en el equipo o el modelo asignado.'
            : undefined,
      };
    }

    const lora = t.tipo === 'T1000-B' ? t.t1000b : undefined;
    const perdida = lora?.paquetes?.perdidaPaquetes;
    const vigentes = this.configsReenvio.filter((c) => configReenvioVigente(c));

    this.vista = {
      marcaModelo: [t.modelo?.marca, t.modelo?.modelo]
        .filter(Boolean)
        .join(' · '),
      asignadoA: t.activo?.identificacion,
      estadoCuenta: t.estadoCuenta ? String(t.estadoCuenta) : undefined,
      uniqueId: t.uniqueId,
      telefono: t.telefono ? String(t.telefono) : undefined,
      numeroAbonado: t.numeroAbonado,
      numeroSerie: t.tipo === 'Qualcomm' ? t.qualcomm?.serialNumber : undefined,
      frecuencia: t.frecReporte ? duracion(t.frecReporte) : undefined,
      conexion: estadoConexion(minutos, t.frecReporte),
      ultimoReporte: fecha ? HelperService.getFechaYHora(fecha) : undefined,
      hace: fecha ? haceCuanto(fecha) : undefined,
      protocolo,
      reenvios: this.puedeVerReenvios
        ? { activas: vigentes.length, total: this.configsReenvio.length }
        : undefined,
      sims: [
        this.simsService.simDetalle(1, t.sim1, t.idSim1),
        this.simsService.simDetalle(2, t.sim2, t.idSim2),
      ],
      lora: lora
        ? {
            deveui: lora.deveui,
            joineui: lora.joineui,
            senal:
              lora.margin !== undefined && lora.margin !== null
                ? `${lora.margin} dB`
                : undefined,
            ultimaComunicacion: lora.fechaUltimaComunicacion
              ? this.helper.formatearFecha(lora.fechaUltimaComunicacion)
              : undefined,
            tasaEntrega:
              perdida === undefined || perdida === null
                ? undefined
                : `${Math.max(0, Math.min(100, 100 - perdida))} %`,
            ultimoGateway: lora.ultimoGateway?.gatewayEui,
            snrGateway: lora.ultimoGateway?.snr,
            limiteComunicacion: lora.tiempoLimiteComunicacion,
            habilitado: !lora.isDisabled,
          }
        : undefined,
    };
  }

  /**
   * Diagnóstico del T1000-B: confirma contra ChirpStack que el device existe
   * realmente, que es lo que puede divergir si el provisioning falló a mitad
   * de camino.
   */
  public async verificarEnChirpstack(): Promise<void> {
    if (!this.tracker?._id) return;
    try {
      const device = await this.trackersService.consultarChirpstack(
        this.tracker._id,
      );
      if (device?.device?.devEui) {
        this.helper.notifSuccess(
          `El device existe en ChirpStack (${device.device.name || device.device.devEui})`,
        );
      } else {
        this.helper.notifError(
          'El device NO existe en el Network Server. Revisar el provisioning.',
        );
      }
    } catch (error) {
      this.helper.notifError(error);
    }
  }

  public verReenvios(): void {
    this.formularios.abrir<
      ReenviosTrackerDialogComponent,
      IReenviosTrackerData
    >(ReenviosTrackerDialogComponent, {
      tamano: 'mediano',
      cerrarAfuera: true,
      data: {
        nombreEntidad:
          this.tracker?.identificacion ?? this.tracker?.nombre ?? undefined,
        configs: this.configsReenvio,
        idTracker: this.tracker?._id,
      },
    });
  }

  /// Aplican al tracker: las de la propia entidad, las del vehículo al que está
  /// asignado y las de "todos los trackers del cliente" (del cliente dueño, o
  /// de un ancestro si reenvía a hijos).
  private async listarConfigsReenvio(): Promise<void> {
    if (!this.puedeVerReenvios || this.configsReenvioPedidas) return;
    const idCliente = this.tracker?.idCliente;
    if (!idCliente) return;
    this.configsReenvioPedidas = true;

    const condiciones: IFilter<IConfigReenvio>[] = [
      { agrupacionReenvio: 'Entidad', idEntidadReenvio: this.id },
      ...(this.tracker?.asignadoA
        ? [
            {
              agrupacionReenvio: 'Entidad',
              idEntidadReenvio: this.tracker.asignadoA,
            },
          ]
        : []),
      {
        agrupacionReenvio: 'Todos los trackers del cliente',
        idClienteReenvio: idCliente,
      },
    ];
    const ancestros = this.tracker?.idsAncestros ?? [];
    if (ancestros.length) {
      condiciones.push({
        agrupacionReenvio: 'Todos los trackers del cliente',
        idClienteReenvio: { $in: ancestros },
        reenviarHijos: true,
      });
    }

    const populate: IPopulate[] = [
      { path: 'clienteReenvio', select: 'nombre' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ $or: condiciones }),
      populate: JSON.stringify(populate),
      includeChildren: true,
      sort: '-fechaCreacion',
    };
    this.configReenvios$?.unsubscribe();
    this.configReenvios$ = this.lista2
      .configReenvios(query)
      .subscribe((data) => {
        this.configsReenvio = data.datos;
        this.armarVista();
      });
  }

  private dataDelmapa(): void {
    if (!this.reporte) {
      this.loadingMap.set(false);
      return;
    }
    const tipo = this.reporte?.tipoReporte;
    const valores =
      tipo === 'Tracker 4G'
        ? (this.reporte.valores as IReporteTracker4G)
        : tipo === 'Tracker T1000B'
          ? (this.reporte.valores as IReporteTrackerT1000B)
          : tipo === 'Tracker Qualcomm'
            ? (this.reporte.valores as IReporteTrackerQualcomm)
            : null;

    const coordinate = valores?.geojson?.coordinates as [number, number];
    if (!coordinate) {
      this.loadingMap.set(false);
      return;
    }

    const marker: MarkerMapa = {
      label: 'puntoSimple',
      coor: coordinate,
      centrar: true,
    };

    this.mapCardConfig = {
      // La fecha del reporte ya está en la cabecera.
      title: 'Última ubicación',
      subtitle: HelperService.extraerDireccion(
        (this.reporte?.valores as any)?.direccion,
      ),
      icon: 'location_on',
      color: 'red',
      mapOptions: {
        markers: [marker],
        center: coordinate,
        zoom: 15,
      },
    };

    this.mapaService.updateData({ markers: [marker], zoom: 15 });
    this.loadingMap.set(false);
  }

  public async listar(): Promise<void> {
    const filter: IFilter<ITracker> = { _id: this.id };
    const populate: IPopulate[] = [
      {
        path: 'cliente',
        select: 'nombre',
      },
      {
        path: 'modelo',
        select: 'modelo marca protocoloTracker',
      },
      {
        path: 'activo',
        select: 'identificacion',
      },
      {
        path: 'sim1',
      },
      {
        path: 'sim2',
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      includeChildren: true,
    };
    this.trackers$?.unsubscribe();
    this.trackers$ = this.lista2.trackers(query, 30).subscribe({
      next: (data) => {
        this.tracker = data.datos[0];
        this.armarVista();
        void this.listarConfigsReenvio();
        // Recién con el tracker: antes, sin vista, mostraba "No se encontró"
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  public async UltimoReporte(): Promise<void> {
    const filter: IFilter<IReporteGenerico> = { idEntidad: this.id };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };
    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .ultimosReportesGenericos(query, 30)
      .subscribe((data) => {
        // Hay un último reporte por tipoReporte; el protocolo detectado solo
        // viene en el de posición ("Tracker 4G").
        this.reporte4G = data.datos.find(
          (r) => r.tipoReporte === 'Tracker 4G',
        ) as IReporteBase<'Tracker 4G'> | undefined;
        // Para el mapa y la fecha, el de posición si existe: el primero puede
        // ser uno de combustible o temperatura, que dataDelmapa no dibuja.
        this.reporteReciente = data.datos[0] as IReporteGenerico | undefined;
        const reporte = this.reporte4G ?? data.datos[0];
        if (reporte) {
          this.reporte = reporte as IReporteGenerico;
          this.dataDelmapa();
        } else {
          this.loadingMap.set(false);
        }
        this.armarVista();
      });
  }

  async ngOnInit() {
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');

    this.mapCardConfig = {
      title: 'Última ubicación',
      icon: 'location_on',
      color: 'red',
    };

    await this.listar();
    await this.UltimoReporte();
  }

  ngOnDestroy() {
    this.observadorMapa?.disconnect();
    this.trackers$?.unsubscribe();
    this.reportes$?.unsubscribe();
    this.configReenvios$?.unsubscribe();
    this.mapaService.clearMap();
  }
}
